import { Colli } from '@frontend/colli/types';
import { PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageTriggerName } from '@frontend/package/types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePackageListener } from '../../../hooks/usePackageListener/usePackageListener';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { fetchOpenableDropOffPackages, isOpenableDropOffPackage } from '../../utils/seed-drop-off-colli-context';
import { useScreen } from '../useScreen';

interface ViewProps {
    error: string | null;
}

//Maybe this screen should just be updated to make sure it also supports pickup ??
//why not just check if the transaction has a package in pikcup for the current location and if so proceed
const useVerifyCurrentLocation = (): ViewProps => {
    const intl = useIntl();
    const { navigationState, store, updateWizardData, onSubmit } = useScreen();
    const [error, setError] = useState<string | null>(null);

    const { transaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });
    const { package: _package, localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store
    });

    usePackageListener({
        id: 'VERIFY_CURRENT_LOCATION',
        onStateChange: (pkg) => {
            if (pkg.id === _package!.id) {
                localUpdatePackage(pkg);
                onSubmit();
            }
        }
    });

    useEffect(() => {
        if (transaction && !_package) {
            const applyPackage = (pkg: Package | undefined) => {
                if (pkg) {
                    localUpdatePackage(pkg);
                    return;
                }
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.verify-current-location.no-package-error',
                        defaultMessage: 'Your transaction is not supposed to be dropped off at this location.',
                        description: 'Error message shown when the user is at the wrong location for dropping off their package'
                    })
                );
            };

            const seededPackages = ((store?.data.packages as Package[] | undefined) ?? []).filter(isOpenableDropOffPackage);
            const collis = (store?.data.collis as Colli[] | undefined) ?? [];
            const spotId = navigationState.spot?.id;

            if (seededPackages.length > 0) {
                updateWizardData({ packages: seededPackages });
                applyPackage(seededPackages[0]);
            } else if (transaction.id && navigationState.iot?.account_id) {
                fetchOpenableDropOffPackages({
                    accountId: navigationState.iot.account_id,
                    transactionId: transaction.id,
                    spotId,
                    colliIds: collis.length > 0 ? collis.map((c) => c.id) : undefined
                }).then((packages) => {
                    if (packages.length > 0) updateWizardData({ packages });
                    applyPackage(packages[0]);
                });
            } else {
                applyPackage(undefined);
            }
        }

        if (_package) {
            PackageWorkflowClient.triggerPackageState(_package.account_id!, _package.transaction_id!, _package.id!, {
                trigger: PackageTriggerName.PACKAGE_CONTINUE,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            if (_package.state && [PackageStateName.PACKAGE_DROP_OFF_PENDING, PackageStateName.PACKAGE_DROP_OFF_STARTED].includes(_package.state)) onSubmit();
        }
    }, [transaction, _package]);

    return {
        error
    };
};

export default useVerifyCurrentLocation;
